import { Schema as S } from "effect";

import { defineApi, staticApis } from "../client";
import { RecordHistory } from "../schemas/records";
import {
	CommentType,
	GradeFilter,
	ScorecardConfigList,
	ScorecardHistory,
	TaskComment,
	TaskCommentList,
	TaskDetail,
	TaskEnvelope,
	TaskPage,
	TaskPriority,
	TaskSort,
	TaskStatus,
	TaskTree,
} from "../schemas/tasks";

/** One page of the circle's tasks; filters go as CSV, `cursor` null starts over. */
export const listTasks = defineApi("ListTasks", {
	args: {
		query: S.String,
		statuses: S.Array(TaskStatus),
		priorities: S.Array(TaskPriority),
		tags: S.Array(S.String),
		graded: GradeFilter,
		sort: TaskSort,
		cursor: S.NullOr(S.String),
		/** Record ids: only tasks about any of these people (the record page's Tasks tab). */
		people: S.optionalKey(S.Array(S.String)),
	},
	response: TaskPage,
	request: ({
		query,
		statuses,
		priorities,
		tags,
		graded,
		sort,
		cursor,
		people,
	}) => ({
		method: "GET",
		path: "/tasks",
		query: {
			query: query.trim(),
			status: statuses.join(","),
			priority: priorities.join(","),
			tags: tags.join(","),
			graded: graded === "all" ? "" : String(graded === "graded"),
			order_by: sort,
			cursor,
			people: people?.join(","),
			limit: 50,
		},
	}),
});

export const createTask = defineApi("CreateTask", {
	args: { title: S.String },
	response: TaskEnvelope,
	request: ({ title }) => ({ method: "POST", path: "/tasks", body: { title } }),
});

/** Optimistic-concurrency write: a stale `ifLastUpdated` answers 412 (StaleRecord). */
export const updateTask = defineApi("UpdateTask", {
	args: {
		id: S.String,
		ifLastUpdated: S.String,
		status: S.optionalKey(TaskStatus),
		priority: S.optionalKey(TaskPriority),
	},
	response: TaskEnvelope,
	request: ({ id, ifLastUpdated, ...body }) => ({
		method: "PATCH",
		path: `/tasks/${encodeURIComponent(id)}`,
		query: { ifLastUpdated },
		body,
	}),
});

const taskPath = (id: string, rest = "") =>
	`/tasks/${encodeURIComponent(id)}${rest}`;

export const getTask = defineApi("GetTask", {
	args: { id: S.String },
	response: TaskDetail,
	request: ({ id }) => ({ method: "GET", path: taskPath(id) }),
});

export const listTaskComments = defineApi("ListTaskComments", {
	args: { id: S.String },
	response: TaskCommentList,
	request: ({ id }) => ({ method: "GET", path: taskPath(id, "/comments") }),
});

export const getTaskTree = defineApi("GetTaskTree", {
	args: { id: S.String },
	response: TaskTree,
	request: ({ id }) => ({ method: "GET", path: taskPath(id, "/tree") }),
});

/** The task's versions, newest first (the record history shape). */
export const getTaskHistory = defineApi("GetTaskHistory", {
	args: { id: S.String },
	response: RecordHistory,
	request: ({ id }) => ({
		method: "GET",
		path: taskPath(id, "/history"),
		query: { limit: 50 },
	}),
});

export const getTaskScorecard = defineApi("GetTaskScorecard", {
	args: { id: S.String },
	response: ScorecardHistory,
	request: ({ id }) => ({ method: "GET", path: taskPath(id, "/scorecard") }),
});

export const { listScorecardConfigs } = staticApis({
	listScorecardConfigs: ["GET", "/scorecard/configs", ScorecardConfigList],
});

/** Runs the circle's default scorecard on the task; the page refetches for the new run. */
export const runTaskScorecard = defineApi("RunTaskScorecard", {
	args: { id: S.String },
	response: S.Unknown,
	request: ({ id }) => ({
		method: "POST",
		path: taskPath(id, "/scorecard"),
		body: {},
	}),
});

/** A typed comment or a reply (`parentCommentId`); no `ifLastUpdated`: comments never conflict. */
export const addTaskComment = defineApi("AddTaskComment", {
	args: {
		id: S.String,
		body: S.String,
		type: CommentType,
		parentCommentId: S.NullOr(S.String),
	},
	response: S.Struct({ comment: TaskComment }),
	request: ({ id, body, type, parentCommentId }) => ({
		method: "POST",
		path: taskPath(id, "/comments"),
		body: { body, type, parent_comment_id: parentCommentId },
	}),
});

/**
 * Copies a section-typed comment into its section's canonicals. The server
 * retries its own compare-and-swap and answers 409 when that runs out.
 */
export const promoteTaskComment = defineApi("PromoteTaskComment", {
	args: { commentId: S.String },
	response: S.Struct({ changed: S.Boolean }),
	request: ({ commentId }) => ({
		method: "POST",
		path: `/tasks/comments/${encodeURIComponent(commentId)}/promote`,
		body: {},
	}),
});

export const TaskSection = S.Literals(["hypothesis", "evidence", "outcome"]);
export type TaskSection = typeof TaskSection.Type;

/** Removes the canonical promoted from `commentId`; the comment itself stays. */
export const demoteTaskCanonical = defineApi("DemoteTaskCanonical", {
	args: { id: S.String, section: TaskSection, commentId: S.String },
	response: S.Struct({ changed: S.Boolean }),
	request: ({ id, section, commentId }) => ({
		method: "DELETE",
		path: taskPath(
			id,
			`/sections/${section}/canonicals/${encodeURIComponent(commentId)}`,
		),
	}),
});
